import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import HelpIcon from '@material-ui/icons/Help'
import SendToMobileIcon from '@material-ui/icons/MobileFriendly'
import _ from 'lodash'
import { Button, useRedirect } from 'react-admin'
import { Link } from 'react-router-dom'
import { Card } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { HosEtaSection, HosETAWarning, TrackingStatusBadge } from 'shared/components/Job/JobTracking'
import { Text } from 'shared/components/Text/Text'
import { getFormattedDate, getTimeDifferenceFromNowInMinutes } from 'shared/helpers/formatters'
import { SnakeToCamelCaseNested } from 'shared/helpers/typeHelper'
import { toCamelCase } from 'shared/helpers/utils'
import { LoadStopProgressStatus, TrackingStatus } from 'shared/models/load'
import { lightGray1, midGray } from 'shared/theme/colors'

import { OnelineAddress } from 'components/Common/Loads'
import { getLocalizedStopAppointmentWindow } from 'helpers/loads'
import { Job, LoadStopProgress } from 'models/jobs'
import { BoxedGridItem } from '../Common/BoxedGridItem'
import MapComponent from './Map'

interface LoadStopProgressSectionProps {
  loadStopProgress: LoadStopProgress
  trackingStatus: TrackingStatus
  showHosEta?: boolean
}

const useStyles = makeStyles({
  addressLine: {
    color: midGray,
  },
  heading: {
    color: lightGray1,
    marginRight: '10px',
  },
  etaBreakdownWrapper: {
    marginLeft: '16px',
    '&:not(:last-child)': {
      borderLeft: `1px solid ${lightGray1}`,
    },
    '&:last-child > $etaSpacer': {
      borderLeft: `1px solid ${lightGray1}`,
    },
  },
  etaBreakdown: {
    padding: '5px',
  },
  etaSpacer: {
    height: '50%',
    width: '16px',
    borderBottom: `1px solid ${lightGray1}`,
  },
  etaSection: {
    paddingLeft: '4px',
  },
})

const LoadStopProgressSection = ({
  loadStopProgress,
  trackingStatus,
  showHosEta = false,
}: LoadStopProgressSectionProps) => {
  const stop = loadStopProgress.stop
  const classes = useStyles()
  const hasArrivedAtStop = [LoadStopProgressStatus.AT_FACILITY, LoadStopProgressStatus.DEPARTED].includes(
    loadStopProgress.status
  )
  const displayHosEtaSection = showHosEta && !hasArrivedAtStop && loadStopProgress.last_schedule_event

  const schedulePath = `/schedules/${
    loadStopProgress && toCamelCase(loadStopProgress).lastScheduleEvent?.scheduleId
  }/show/feasibility`

  return (
    <Grid container item direction="row" justifyContent="flex-start" spacing={0}>
      <TrackingStatusBadge
        hasArrivedAtStop={hasArrivedAtStop}
        trackingStatus={trackingStatus}
        loadStopProgress={toCamelCase(loadStopProgress)}
        showHosEta={Boolean(displayHosEtaSection)}
      />
      <Grid container item xs={10} direction="column" spacing={1} justifyContent="flex-start">
        <Grid container item direction="row">
          <Grid container item direction="column" xs={4} spacing={1}>
            <Grid item>
              <Typography variant="body2">{`STOP ${stop.sequence_number} ${_.startCase(
                stop.type_detail.toLowerCase()
              )} ${_.startCase(stop.loading_type?.toLowerCase())}`}</Typography>
            </Grid>
            {stop.location_name && (
              <Grid item>
                <Typography variant="body2">{stop.location_name}</Typography>
              </Grid>
            )}
            <Grid item>
              <OnelineAddress record={stop} label={classes.addressLine} />
            </Grid>
          </Grid>
          <Grid item container direction="column" xs={4} spacing={1}>
            <BoxedGridItem
              heading="Appointment"
              value={getLocalizedStopAppointmentWindow(stop)}
              classes={classes}
            />
            {loadStopProgress.date_arrived && (
              <BoxedGridItem
                heading="Arrived"
                value={getFormattedDate(loadStopProgress.date_arrived, stop.location_timezone)}
                classes={classes}
              />
            )}
            {loadStopProgress.date_departed && (
              <BoxedGridItem
                heading="Departed"
                value={getFormattedDate(loadStopProgress.date_departed, stop.location_timezone)}
                classes={classes}
              />
            )}
            {displayHosEtaSection && (
              <Grid container item direction="row" spacing={1}>
                <Grid item>
                  <HosEtaSection<SnakeToCamelCaseNested<LoadStopProgress>>
                    loadStopProgress={toCamelCase(loadStopProgress)}
                    trackingStatus={trackingStatus}
                    titleVariant="label3"
                    labelVariant="label3"
                    textVariant="label3"
                    className={classes.etaSection}
                  />
                </Grid>
                <Grid item>
                  <Tooltip placement="right" title="View the schedule generating this eta.">
                    <Link to={schedulePath}>
                      <HelpIcon fontSize="small" />
                    </Link>
                  </Tooltip>
                </Grid>
              </Grid>
            )}
            {!displayHosEtaSection && loadStopProgress.date_estimated_arrival && (
              <BoxedGridItem
                heading="Estimated"
                value={getFormattedDate(loadStopProgress.date_estimated_arrival, stop.location_timezone)}
                classes={classes}
                isEta
              />
            )}
          </Grid>
        </Grid>
        <Grid container item style={{ width: '60%' }} direction="column" spacing={3}>
          {displayHosEtaSection &&
            loadStopProgress.is_current &&
            trackingStatus === TrackingStatus.LATE &&
            loadStopProgress.last_schedule_event.off_duty_hours > 0 && <HosETAWarning />}
        </Grid>
      </Grid>
    </Grid>
  )
}

interface JobTrackingProps {
  record: Job
  isMobile: boolean
  loadStopProgresses: LoadStopProgress[]
  trackingCurrentLoadStopProgress: LoadStopProgress
  trackingStatus: TrackingStatus
  showHosEta?: boolean
}

export const JobTracking = ({
  record,
  isMobile,
  loadStopProgresses,
  trackingCurrentLoadStopProgress,
  trackingStatus,
  showHosEta = false,
}: JobTrackingProps) => {
  const dataSourceString = isMobile ? 'Mobile' : 'ELD'
  let latestLocationVector = isMobile ? record?.latest_mobile_location : record?.latest_truck_location
  if (!latestLocationVector) {
    latestLocationVector = trackingCurrentLoadStopProgress?.tracking_last_location_vector
  }
  return (
    <>
      <Grid container direction="column" justifyContent="flex-start" spacing={4}>
        <Grid item>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box display="flex">
              <Typography variant="h5">{dataSourceString} Location Tracking</Typography>
              {isMobile && <SendToMobileIcon />}
            </Box>
          </Box>
        </Grid>
        {!!loadStopProgresses?.length && !latestLocationVector && (
          <Grid item>
            <Typography variant="body1">{dataSourceString} tracking available after job starts.</Typography>
          </Grid>
        )}
        {!loadStopProgresses?.length && !latestLocationVector && (
          <Grid item>
            <Typography variant="body1">No {dataSourceString} tracking available.</Typography>
          </Grid>
        )}
        {loadStopProgresses?.length > 0 &&
          loadStopProgresses.map((loadStopProgress: LoadStopProgress) => (
            <LoadStopProgressSection
              key={loadStopProgress.stop.sequence_number}
              loadStopProgress={loadStopProgress}
              trackingStatus={trackingStatus}
              showHosEta={showHosEta}
            />
          ))}
        {latestLocationVector && (
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            style={{ width: '60%' }}
            spacing={0}
          >
            <MapComponent
              latestLocationVector={latestLocationVector}
              currentStop={trackingCurrentLoadStopProgress.stop}
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}

export const JobTrackingCard = ({ job }: { job: Job }) => {
  const redirect = useRedirect()

  const currentLoadStopProgress = job.tracking_current_eld_load_stop_progress
    ? job.tracking_current_eld_load_stop_progress
    : job.tracking_current_load_stop_progress

  const currentStop = currentLoadStopProgress?.stop
  const trackingStatus = job.eld_tracking_status
    ? job.hos_eld_tracking_status
    : job.hos_mobile_tracking_status
  const hasArrivedAtStop = [LoadStopProgressStatus.AT_FACILITY, LoadStopProgressStatus.DEPARTED].includes(
    currentLoadStopProgress?.status
  )
  const stopTypeDetail = currentStop?.type_detail.toLowerCase()

  return currentLoadStopProgress?.last_schedule_event ? (
    <Card fullWidth smallerPadding noShadow height="fit-content">
      <FlexBox justifyContent="flex-start" alignItems="flex-start" flexDirection="column">
        <FlexBox flexDirection="column" alignItems="flex-start">
          <Text variant="uppercaseLabel3" fontColor={midGray}>
            Next Stop
          </Text>
          {currentLoadStopProgress && currentStop && (
            <Text
              variant="header3"
              style={{
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                marginTop: '10px',
                marginBottom: '15px',
              }}
            >
              {currentStop?.location_city}, {currentStop?.location_state} • {stopTypeDetail}
            </Text>
          )}
          <TrackingStatusBadge<SnakeToCamelCaseNested<LoadStopProgress>>
            hasArrivedAtStop={hasArrivedAtStop}
            loadStopProgress={toCamelCase(currentLoadStopProgress)}
            trackingStatus={trackingStatus}
            showHosEta={true}
          />
        </FlexBox>
        <FlexBox justifyContent="space-between" alignItems="center" width="100%" mt={8}>
          <Text variant="paragraph3" fontColor={lightGray1} style={{ whiteSpace: 'nowrap' }}>
            Updated {getTimeDifferenceFromNowInMinutes(currentLoadStopProgress?.date_updated)} minutes ago
          </Text>
          <Button
            label="See details"
            style={{ textTransform: 'none', fontSize: '14px' }}
            onClick={() => redirect(`/jobs/${job.id}/show/route`)}
          />
        </FlexBox>
      </FlexBox>
    </Card>
  ) : null
}
